import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { ArrowLeft, Search, Download, ExternalLink, Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * /dashboard/free-photos — Family-safe free stock photos via Pexels.
 *
 * Members can search, preview large, and download originals. All images are
 * royalty-free for personal AND commercial use under the Pexels license
 * (must credit the photographer if redistributing on a stand-alone gallery —
 * not required for courses, blogs, sales pages, social).
 */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const CREAM = "#FFFFFF";

const STARTER_QUERIES = [
  "country church", "praying hands", "open Bible", "grandmother kitchen",
  "sunrise field", "family dinner", "front porch", "Jesus light",
  "small town main street", "wholesome family", "Sunday morning",
];

export default function FreePhotosPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);   // currently-zoomed photo

  const runSearch = async (query, p = 1) => {
    setLoading(true);
    if (p === 1) setPhotos([]);
    try {
      const { data } = await api.get("/photos/search", {
        params: { q: query, page: p, per_page: 24 },
      });
      setPhotos((prev) => (p === 1 ? data.photos : [...prev, ...data.photos]));
      setTotal(data.total_results || 0);
      setPage(data.page || p);
    } catch {
      toast.error("Couldn't reach the photo library.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load — show curated feed
  useEffect(() => { if (user) runSearch(""); }, [user]);

  if (user === null) return <Navigate to="/login" replace />;
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    runSearch(q, 1);
  };

  const tryStarter = (s) => { setQ(s); setPage(1); runSearch(s, 1); };

  const download = async (photo) => {
    try {
      const r = await fetch(photo.download);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `faitharama-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Downloaded");
    } catch {
      // Fallback: open in new tab
      window.open(photo.download, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="free-photos-page">
      <SiteNav />

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <Link to="/dashboard"
              data-testid="free-photos-back-btn"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
              style={{ color: WINE }}>
          <ArrowLeft className="w-3.5 h-3.5" /> My Office
        </Link>

        <div className="mt-4 mb-5">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GOLD }}>
            ★ Free Photo Library
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl uppercase leading-tight mt-1"
              style={{ color: NAVY }}>
            Free Family-Safe Photos.
          </h1>
          <p className="text-base md:text-lg italic mt-2" style={{ color: "#3D3D3D" }}>
            Grab high-quality photos for your courses, sales pages, social posts, anything you build. All free.
          </p>
        </div>

        {/* Search row */}
        <form onSubmit={onSearch}
              data-testid="free-photos-search-form"
              className="flex gap-2 items-stretch">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#7A7066" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search photos — country church, family, sunrise, Bible…"
              data-testid="free-photos-search-input"
              className="w-full pl-10 pr-4 py-3 rounded-full border-2 text-base outline-none focus:border-[#E8B539]"
              style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
            />
          </div>
          <button type="submit"
                  disabled={loading}
                  data-testid="free-photos-search-btn"
                  className="px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow disabled:opacity-50"
                  style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </form>

        {/* Starter chips */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="free-photos-starters">
          {STARTER_QUERIES.map((s) => (
            <button key={s}
                    type="button"
                    onClick={() => tryStarter(s)}
                    data-testid={`starter-${s.replace(/\s/g, "-")}`}
                    className="text-xs px-3 py-1.5 rounded-full border bg-white hover:border-[#E8B539] hover:text-[#0F172A] transition"
                    style={{ borderColor: "#D6D2C9", color: "#3D3D3D" }}>
              {s}
            </button>
          ))}
        </div>

        {/* Family-safe note */}
        <div className="mt-4 rounded-md px-3 py-2 text-xs flex items-start gap-2"
             style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55`, color: "#3D3D3D" }}>
          <Camera className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: WINE }} />
          <span>
            Family-safe content only. Photos are royalty-free for use in your courses, sales pages, emails, and social posts.
            Photo credit is appreciated but not required. <a href="https://www.pexels.com/license/" target="_blank" rel="noopener noreferrer" className="underline">License details →</a>
          </span>
        </div>

        {/* Total */}
        {total > 0 && (
          <div className="mt-4 text-xs" style={{ color: "#7A7066" }}>
            {total.toLocaleString()} photos found
          </div>
        )}

        {/* Grid */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
             data-testid="free-photos-grid">
          {photos.map((p) => (
            <button key={p.id}
                    type="button"
                    onClick={() => setPreview(p)}
                    data-testid={`photo-${p.id}`}
                    className="group rounded-lg overflow-hidden border-2 bg-white hover:shadow-xl hover:-translate-y-0.5 transition text-left"
                    style={{ borderColor: "#E5E1D6" }}>
              <div className="relative aspect-[4/3] overflow-hidden bg-white">
                <img src={p.thumb} alt={p.alt}
                     className="w-full h-full object-cover group-hover:scale-[1.03] transition" loading="lazy" />
              </div>
              <div className="p-2 text-[10px]" style={{ color: "#7A7066" }}>
                by {p.photographer}
              </div>
            </button>
          ))}
        </div>

        {/* Empty / loading state */}
        {!loading && photos.length === 0 && (
          <div className="rounded-2xl border-2 bg-white p-10 text-center mt-6"
               style={{ borderColor: "#E5E1D6" }}>
            <div className="font-bold text-lg" style={{ color: NAVY }}>No photos found.</div>
            <div className="text-sm mt-1" style={{ color: "#7A7066" }}>
              Try a different search.
            </div>
          </div>
        )}

        {/* Load more */}
        {photos.length > 0 && (
          <div className="text-center mt-6">
            <button type="button"
                    disabled={loading}
                    onClick={() => runSearch(q, page + 1)}
                    data-testid="free-photos-load-more"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider border-2 hover:bg-white transition disabled:opacity-50"
                    style={{ borderColor: NAVY, color: NAVY }}>
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : "Load More Photos"}
            </button>
          </div>
        )}
      </section>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
             data-testid="photo-preview-modal"
             onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
               onClick={(e) => e.stopPropagation()}
               style={{ border: `4px solid ${GOLD}` }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#E5E1D6" }}>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>★ Preview</div>
                <div className="text-sm" style={{ color: NAVY }}>
                  by{" "}
                  <a href={preview.photographer_url} target="_blank" rel="noopener noreferrer"
                     className="font-bold underline">{preview.photographer}</a>
                  {" "}on Pexels
                </div>
              </div>
              <button type="button" onClick={() => setPreview(null)} className="p-1.5 rounded-full hover:bg-white">
                <X className="w-5 h-5" style={{ color: NAVY }} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-white/60">
              <img src={preview.preview} alt={preview.alt}
                   className="block max-w-full max-h-[60vh] mx-auto" />
              {preview.alt && (
                <div className="text-xs mt-3 italic text-center" style={{ color: "#7A7066" }}>
                  {preview.alt}
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t flex flex-wrap gap-2 justify-end" style={{ borderColor: "#E5E1D6" }}>
              <a href={preview.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border-2 hover:bg-white"
                 style={{ borderColor: NAVY, color: NAVY }}>
                <ExternalLink className="w-3.5 h-3.5" /> Open On Pexels
              </a>
              <button type="button"
                      onClick={() => download(preview)}
                      data-testid="photo-download-btn"
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-xs font-bold uppercase tracking-wider shadow"
                      style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
